import { useEffect, useState } from "react";

import { v4 as uuid } from "uuid";
import { FormikProps } from "formik";
import dayjs from "dayjs";

import Form from "react-bootstrap/Form";
import Col from "react-bootstrap/Col";

import { Option } from "@/@types/generics/Option";
import { TypeofDiagnosticHypothesisFilterInitialValues } from "@/utils/initialValues/diagnosticHypothesisFilter";

import { RedAsterisk } from "@/components/RedAsterisk";
import { FormattedControl } from "@/components/FormattedControl";
import { GenderSelect } from "@/components/sintegrada/Selects/GenderSelect";
import { AgeGroup } from "@/components/sintegrada/AgeGroup";
import { IcdSelect } from "@/components/sintegrada/Selects/IcdSelect";
import {
	ClinicOrSpecialtyOptionValue,
	ClinicOrSpecialtySelect,
} from "@/components/sintegrada/Selects/ClinicOrSpecialtySelect";
import { ProfessionalSelect } from "@/components/sintegrada/Selects/ProfessionalSelect";
import { RiskClassificationSelect } from "@/components/sintegrada/Selects/RiskClassificationSelect";
import { ApplyFilterButton } from "@/components/sintegrada/Buttons/ApplyFilterButton";
import { ExportExcelButton } from "@/components/sintegrada/Buttons/ExportExcelButton";

import "./styles.scss";

type Props = {
	disabledExportButton: boolean;
	handleExportExcel: () => Promise<void>;
	formik: FormikProps<TypeofDiagnosticHypothesisFilterInitialValues>;
};

export function DiagnosticHypothesisFilter({
	disabledExportButton,
	handleExportExcel,
	formik,
}: Props) {
	const [professionalSelectKey, setProfessionalSelectKey] = useState(uuid());

	const { values, touched, errors } = formik;

	const specialty = values.clinicOrSpecialty?.value?.specialty;
	const specialtyCbo = specialty?.cbo?.substring(0, 4);

	const minDate = dayjs().subtract(2, "years").startOf("day").format("YYYY-MM-DDTHH:mm");
	const maxDate = dayjs().endOf("day").format("YYYY-MM-DDTHH:mm");

	function handleChangeMinAge(value: string) {
		formik.setFieldValue("minAge", value);
	}

	function handleChangeMaxAge(value: string) {
		formik.setFieldValue("maxAge", value);
	}

	function handleChangeClinicOrSpecialty(option: Option<ClinicOrSpecialtyOptionValue>) {
		formik.setFieldValue("clinicOrSpecialty", option);
	}

	useEffect(() => {
		setProfessionalSelectKey(uuid());

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [values.clinicOrSpecialty]);

	return (
		<Form className="mb-8" onSubmit={formik.handleSubmit}>
			<Col md={12} className="d-flex gap-4 mb-4">
				<div className="w-25">
					<Form.Label title="Data inicial" htmlFor="start-date">
						Data inicial <RedAsterisk />
					</Form.Label>

					<FormattedControl
						id="start-date"
						type="datetime-local"
						min={minDate}
						isInvalid={touched.startDate && !!errors.startDate}
						{...formik.getFieldProps("startDate")}
					/>

					{touched.startDate && errors.startDate && (
						<Form.Control.Feedback type="invalid">{errors.startDate}</Form.Control.Feedback>
					)}
				</div>

				<div className="w-25">
					<Form.Label title="Data final" htmlFor="end-date">
						Data final <RedAsterisk />
					</Form.Label>

					<FormattedControl
						id="end-date"
						type="datetime-local"
						max={maxDate}
						isInvalid={touched.endDate && !!errors.endDate}
						{...formik.getFieldProps("endDate")}
					/>

					{touched.endDate && errors.endDate && (
						<Form.Control.Feedback type="invalid">{errors.endDate}</Form.Control.Feedback>
					)}
				</div>

				<div className="w-25">
					<Form.Label title="Setor" htmlFor="clinic-or-specialty">
						Setor <RedAsterisk />
					</Form.Label>

					<ClinicOrSpecialtySelect
						inputId="clinic-or-specialty"
						showInternalDepartments
						value={values.clinicOrSpecialty}
						handleChangeClinicOrSpecialty={handleChangeClinicOrSpecialty}
					/>

					{touched.clinicOrSpecialty && errors.clinicOrSpecialty && (
						<span className="text-danger">{errors.clinicOrSpecialty.label}</span>
					)}
				</div>

				<div className="w-25 min-width-0 flex-shrink-1 flex-grow-0">
					<Form.Label title="CID - 10" htmlFor="icd">
						CID - 10 <RedAsterisk />
					</Form.Label>

					<IcdSelect
						inputId="icd"
						value={values.icd}
						onChange={(option) => formik.setFieldValue("icd", option)}
					/>

					{touched.icd && errors.icd && (
						<span className="text-danger">{errors.icd.label}</span>
					)}
				</div>
			</Col>

			<Col md={12} className="d-flex gap-4 mb-4">
				<div className="w-25">
					<RiskClassificationSelect
						value={values.riskClassification}
						onChange={(option) => formik.setFieldValue("riskClassification", option)}
					/>
				</div>

				<div className="w-25 d-flex gap-4">
					<div className="w-50">
						<Form.Label title="Gênero" htmlFor="gender">
							Gênero
						</Form.Label>

						<GenderSelect
							value={values.gender}
							onChange={(option) => formik.setFieldValue("gender", option)}
						/>
					</div>

					<AgeGroup
						minAge={values.minAge}
						maxAge={values.maxAge}
						handleChangeMinAge={handleChangeMinAge}
						handleChangeMaxAge={handleChangeMaxAge}
					/>
				</div>

				<div className="w-25">
					<Form.Label className="form-label" htmlFor="professional">
						Profissional
					</Form.Label>

					<ProfessionalSelect
						inputId="professional"
						key={professionalSelectKey}
						value={values.professional}
						cbos={[specialtyCbo]}
						onChange={(option) => formik.setFieldValue("professional", option)}
					/>
				</div>

				<div className="d-flex gap-4 w-25">
					<div
						id="container-group-by-professional"
						className="d-flex align-items-end mx-8 mb-1"
					>
						<Form.Check
							id="group-by-professional"
							label="Agrupar por profissional"
							{...formik.getFieldProps("groupByProfessional")}
						/>
					</div>

					<div className="d-flex align-items-end gap-4">
						<ApplyFilterButton type="submit" disabled={formik.isSubmitting || !formik.isValid} />

						<ExportExcelButton
							onClick={handleExportExcel}
							disabledExportButton={disabledExportButton}
						/>
					</div>
				</div>
			</Col>
		</Form>
	);
}